import { chatCooldownUntilAtomFamily } from '@/atoms/chatCooldownAtoms';
import {
  addToQueueAtom,
  chatQueueAtomFamily,
  clearQueueAtom,
  generateQueueId,
  popFromQueueAtom,
  promoteQueuedMessageAtom,
  type QueuedFileMetadata,
  type QueuedMessage,
  removeFromQueueAtom,
  toggleAutoSendAtom,
  updateQueuedMessageAtom,
} from '@/atoms/chatQueueAtoms';
import { isCooldownActive } from '@/components/chat/utils/chatCooldown';
import type { ChipOrigin, ChipSurface, MessageEntryMethod } from '@heidi/evidence/contracts';
import { logger } from '@/utils/logger/logger';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';

interface UseChatQueueParams {
  /** The stateKey for this chat (e.g., "chat-123" or "sessionless") */
  stateKey: string;
  /** Whether the chat is currently streaming */
  isStreaming: boolean;
  /** Function to send a message */
  sendMessage: (params: {
    content: string;
    files?: File[];
    attachmentIds?: string[];
    fileMetadata?: QueuedFileMetadata[];
    entryMethod?: MessageEntryMethod;
    chipLabel?: string;
    chipCategory?: string;
    chipOrigin?: ChipOrigin;
    chipSurface?: ChipSurface;
    queued?: boolean;
    queueDurationMs?: number;
  }) => Promise<void>;
  /** Function to abort the current stream */
  abortStream?: () => void;
}

interface UseChatQueueReturn {
  /** Queued messages */
  queuedMessages: QueuedMessage[];
  /** Whether auto-send is enabled */
  autoSendEnabled: boolean;
  /** Whether there are messages in queue */
  hasQueuedMessages: boolean;
  /** Queue count */
  queueCount: number;
  /** Add a message to the queue (returns true if queued, false if sent directly) */
  queueOrSend: (
    content: string,
    files?: File[],
    attachmentIds?: string[],
    fileMetadata?: QueuedFileMetadata[],
    entryMethod?: MessageEntryMethod,
    chipLabel?: string,
    chipCategory?: string,
    chipOrigin?: ChipOrigin,
    chipSurface?: ChipSurface,
  ) => Promise<boolean>;
  /** Remove a message from the queue */
  removeFromQueue: (messageId: string) => void;
  /** Send a queued message immediately (interrupts current stream) */
  sendNow: (messageId: string) => void;
  /** Toggle auto-send setting */
  toggleAutoSend: (enabled?: boolean) => void;
  /** Clear all queued messages */
  clearQueue: () => void;
  /** Edit a queued message */
  updateQueuedMessage: (messageId: string, content: string) => void;
  /** Move a message to front of queue */
  promoteMessage: (messageId: string) => void;
}

/**
 * Hook for managing the chat message queue.
 *
 * Provides Cursor-style message queuing:
 * - When streaming, new messages go to queue instead of interrupting
 * - Users can "Send Now" to interrupt and send immediately
 * - Auto-send: when stream completes, next queued message sends automatically
 * - Queue can be edited, reordered, or cleared
 */
export const useChatQueue = ({
  stateKey,
  isStreaming,
  sendMessage,
  abortStream,
}: UseChatQueueParams): UseChatQueueReturn => {
  const queueAtom = chatQueueAtomFamily(stateKey);
  const queueState = useAtomValue(queueAtom);
  const cooldownUntil = useAtomValue(chatCooldownUntilAtomFamily(stateKey));
  const cooldownActive = isCooldownActive(cooldownUntil);
  const addToQueue = useSetAtom(addToQueueAtom);
  const removeFromQueueAction = useSetAtom(removeFromQueueAtom);
  const popFromQueue = useSetAtom(popFromQueueAtom);
  const toggleAutoSendAction = useSetAtom(toggleAutoSendAtom);
  const clearQueueAction = useSetAtom(clearQueueAtom);
  const updateQueuedMessageAction = useSetAtom(updateQueuedMessageAtom);
  const promoteQueuedMessageAction = useSetAtom(promoteQueuedMessageAtom);

  const { messages: queuedMessages, autoSendEnabled } = queueState;
  const hasQueuedMessages = queuedMessages.length > 0;
  const queueCount = queuedMessages.length;

  // Track if we just stopped streaming (for auto-send)
  const wasStreamingRef = useRef(isStreaming);
  const wasCoolingDownRef = useRef(cooldownActive);

  // Auto-send next queued message when streaming completes, unless a
  // rate-limit cooldown is still running. When that wait ends, send then.
  useEffect(() => {
    const wasStreaming = wasStreamingRef.current;
    const wasCoolingDown = wasCoolingDownRef.current;
    wasStreamingRef.current = isStreaming;
    wasCoolingDownRef.current = cooldownActive;

    const streamJustFinished = wasStreaming && !isStreaming;
    const cooldownJustEnded = wasCoolingDown && !cooldownActive;
    if (
      (streamJustFinished || cooldownJustEnded) &&
      !isStreaming &&
      !cooldownActive &&
      autoSendEnabled &&
      hasQueuedMessages
    ) {
      const nextMessage = popFromQueue(stateKey);
      if (nextMessage) {
        // Measure queue wait at pop time (before the 100ms UI delay so we
        // don't inflate the duration by our own scheduling latency).
        const queueDurationMs = Math.max(0, Date.now() - nextMessage.queuedAt);
        // Small delay to allow UI to update
        setTimeout(() => {
          void sendMessage({
            content: nextMessage.content,
            files: nextMessage.files,
            attachmentIds: nextMessage.attachmentIds,
            fileMetadata: nextMessage.fileMetadata,
            entryMethod: nextMessage.entryMethod,
            chipLabel: nextMessage.chipLabel,
            chipCategory: nextMessage.chipCategory,
            chipOrigin: nextMessage.chipOrigin,
            chipSurface: nextMessage.chipSurface,
            queued: true,
            queueDurationMs,
          }).catch((error: unknown) => {
            logger.error('Failed to auto-send queued chat message', {
              error,
              stateKey,
            });
          });
        }, 100);
      }
    }
  }, [
    isStreaming,
    cooldownActive,
    autoSendEnabled,
    hasQueuedMessages,
    popFromQueue,
    stateKey,
    sendMessage,
  ]);

  /**
   * Queue a message if streaming, or send directly if not.
   * Returns true if message was queued, false if sent directly.
   */
  const queueOrSend = useCallback(
    async (
      content: string,
      files?: File[],
      attachmentIds?: string[],
      fileMetadata?: QueuedFileMetadata[],
      entryMethod?: MessageEntryMethod,
      chipLabel?: string,
      chipCategory?: string,
      chipOrigin?: ChipOrigin,
      chipSurface?: ChipSurface,
    ): Promise<boolean> => {
      if (!content.trim()) return false;

      if (isStreaming || cooldownActive) {
        addToQueue({ stateKey, content, files, attachmentIds, fileMetadata, entryMethod, chipLabel, chipCategory, chipOrigin, chipSurface });
        return true;
      } else {
        // Immediate dispatch: explicitly mark queued=false so analytics has a
        // non-null value on every user message.
        await sendMessage({ content, files, attachmentIds, fileMetadata, entryMethod, chipLabel, chipCategory, chipOrigin, chipSurface, queued: false });
        return false;
      }
    },
    [isStreaming, cooldownActive, addToQueue, stateKey, sendMessage]
  );

  /**
   * Remove a message from the queue.
   */
  const removeFromQueue = useCallback(
    (messageId: string) => {
      removeFromQueueAction({ stateKey, messageId });
    },
    [removeFromQueueAction, stateKey]
  );

  /**
   * Send a queued message immediately, interrupting the current stream.
   */
  const sendNow = useCallback(
    (messageId: string) => {
      if (cooldownActive) return;

      const message = queuedMessages.find((m) => m.id === messageId);
      if (!message) return;

      // Remove from queue first
      removeFromQueueAction({ stateKey, messageId });

      // Capture wait before we abort + schedule the send-now dispatch.
      const queueDurationMs = Math.max(0, Date.now() - message.queuedAt);

      // Abort current stream if needed
      if (isStreaming && abortStream) {
        abortStream();
      }

      setTimeout(() => {
        void sendMessage({
          content: message.content,
          files: message.files,
          attachmentIds: message.attachmentIds,
          fileMetadata: message.fileMetadata,
          entryMethod: message.entryMethod,
          chipLabel: message.chipLabel,
          chipCategory: message.chipCategory,
          chipOrigin: message.chipOrigin,
          chipSurface: message.chipSurface,
          queued: true,
          queueDurationMs,
        }).catch((error: unknown) => {
          logger.error('Failed to send queued chat message immediately', {
            error,
            stateKey,
          });
        });
      }, 50);
    },
    [
      queuedMessages,
      removeFromQueueAction,
      stateKey,
      isStreaming,
      cooldownActive,
      abortStream,
      sendMessage,
    ]
  );

  /**
   * Toggle auto-send setting.
   */
  const toggleAutoSend = useCallback(
    (enabled?: boolean) => {
      toggleAutoSendAction({ stateKey, enabled });
    },
    [toggleAutoSendAction, stateKey]
  );

  /**
   * Clear all queued messages.
   */
  const clearQueue = useCallback(() => {
    clearQueueAction(stateKey);
  }, [clearQueueAction, stateKey]);

  /**
   * Update a queued message's content.
   */
  const updateQueuedMessage = useCallback(
    (messageId: string, content: string) => {
      updateQueuedMessageAction({ stateKey, messageId, content });
    },
    [updateQueuedMessageAction, stateKey]
  );

  /**
   * Move a message to the front of the queue.
   */
  const promoteMessage = useCallback(
    (messageId: string) => {
      promoteQueuedMessageAction({ stateKey, messageId });
    },
    [promoteQueuedMessageAction, stateKey]
  );

  return {
    queuedMessages,
    autoSendEnabled,
    hasQueuedMessages,
    queueCount,
    queueOrSend,
    removeFromQueue,
    sendNow,
    toggleAutoSend,
    clearQueue,
    updateQueuedMessage,
    promoteMessage,
  };
};

export { generateQueueId, type QueuedFileMetadata, type QueuedMessage };
